import { useEffect, useState } from 'react';
import { Download, X, Share, Plus } from 'lucide-react';

const DISMISS_KEY = 'redflag_pwa_install_dismissed_at';
const DISMISS_DAYS = 14;

function isIos() {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent) && !/crios|fxios|edgios/i.test(navigator.userAgent);
}

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    // iOS Safari
    window.navigator.standalone === true
  );
}

function recentlyDismissed() {
  try {
    const at = localStorage.getItem(DISMISS_KEY);
    if (!at) return false;
    const ts = parseInt(at, 10);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

/**
 * Floating install prompt. Two flows:
 *  - Android/Chrome/Edge: captures `beforeinstallprompt`, shows "Installieren"
 *  - iOS Safari: shows manual instructions (Share -> "Zum Home-Bildschirm")
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };

    window.addEventListener('beforeinstallprompt', onPrompt);

    // iOS hint
    if (isIos()) {
      const t = setTimeout(() => setShow(true), 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', onPrompt);
      };
    }

    return () => window.removeEventListener('beforeinstallprompt', onPrompt);
  }, []);

  useEffect(() => {
    const onInstalled = () => {
      setShow(false);
      setDeferred(null);
    };
    window.addEventListener('appinstalled', onInstalled);
    return () => window.removeEventListener('appinstalled', onInstalled);
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* noop */
    }
    setShow(false);
    setIosHint(false);
  };

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      const choice = await deferred.userChoice.catch(() => null);
      if (choice?.outcome !== 'accepted') {
        dismiss();
      }
      setDeferred(null);
      setShow(false);
      return;
    }
    if (isIos()) {
      setIosHint(true);
    }
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-4 z-[60] flex justify-center px-4 sm:bottom-6"
      data-testid="pwa-install-prompt"
    >
      <div className="relative w-full max-w-md overflow-hidden rounded-xl border border-neutral-800 bg-neutral-950/95 shadow-2xl backdrop-blur">
        <div className="absolute inset-y-0 left-0 w-1 bg-red-600" />
        <div className="flex items-start gap-3 p-4 pr-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-red-600/15 text-red-500">
            <Download size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-white">
              RED FLAG als App installieren
            </p>
            <p className="mt-1 text-xs leading-relaxed text-neutral-400">
              Schneller Zugriff auf SelfTest, Podcast und Hilfe — direkt vom Startbildschirm. Funktioniert auch offline.
            </p>

            {iosHint && (
              <div className="mt-3 rounded-md border border-neutral-800 bg-neutral-900 p-3 text-xs text-neutral-300" data-testid="pwa-ios-hint">
                <p className="mb-2 font-semibold text-white">So gehts auf iPhone/iPad:</p>
                <ol className="space-y-1.5">
                  <li className="flex items-center gap-2">
                    <Share size={14} className="text-red-500" />
                    <span>Tippe unten auf <strong>Teilen</strong></span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Plus size={14} className="text-red-500" />
                    <span>Wähle <strong>„Zum Home-Bildschirm“</strong></span>
                  </li>
                </ol>
              </div>
            )}

            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={install}
                className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-white transition hover:bg-red-700"
                data-testid="pwa-install-btn"
              >
                <Download size={14} />
                {isIos() && !deferred ? 'Anleitung' : 'Installieren'}
              </button>
              <button
                type="button"
                onClick={dismiss}
                className="rounded-md px-3 py-1.5 text-xs font-medium text-neutral-400 transition hover:text-white"
                data-testid="pwa-install-dismiss"
              >
                Später
              </button>
            </div>
          </div>
          <button
            type="button"
            onClick={dismiss}
            aria-label="Schließen"
            className="rounded-md p-1 text-neutral-500 transition hover:bg-neutral-800 hover:text-white"
            data-testid="pwa-install-close"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
